import { useCallback, useEffect, useRef, useState } from "react";
import type {
	AssistantLogConversationCardDto,
	AssistantLogUserDetailDto,
} from "@/types/ai-assistant-log";
import { fetchLogUserDetail } from "./log.api";
import type { AssistantLogFilters } from "./use-assistant-log";

/** Ukuran halaman daftar percakapan (kursor). */
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;
const DEFAULT_RANGE_DAYS = 30;

export interface AssistantLogUserState {
	/** null = belum pernah selesai dimuat. */
	detail: AssistantLogUserDetailDto | null;
	/** Kartu percakapan terkumpul lintas halaman kursor. */
	conversations: AssistantLogConversationCardDto[];
	nextCursor: string | null;
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
	/** true = pengguna tidak ada (404). */
	notFound: boolean;
	filters: AssistantLogFilters;
	setFilters: (next: AssistantLogFilters) => void;
	search: string;
	setSearch: (next: string) => void;
	loadMore: () => void;
}

const DEFAULT_FILTERS: AssistantLogFilters = { days: DEFAULT_RANGE_DAYS };

/**
 * Lapis 2: profil + ringkasan + daftar percakapan satu pengguna. Halaman
 * pertama dimuat ulang saat filter/kata kunci berubah; "muat lebih banyak"
 * menambah kartu dari kursor berikutnya tanpa mengulang halaman awal.
 */
export function useAssistantLogUser(userId: string): AssistantLogUserState {
	const [filters, setFiltersState] =
		useState<AssistantLogFilters>(DEFAULT_FILTERS);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [detail, setDetail] = useState<AssistantLogUserDetailDto | null>(null);
	const [conversations, setConversations] = useState<
		AssistantLogConversationCardDto[]
	>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [notFound, setNotFound] = useState(false);
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		const timer = setTimeout(
			() => setDebouncedSearch(search),
			SEARCH_DEBOUNCE_MS,
		);
		return () => clearTimeout(timer);
	}, [search]);

	const setFilters = useCallback((next: AssistantLogFilters) => {
		setFiltersState(next);
	}, []);

	useEffect(() => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		setError(null);
		setNotFound(false);
		fetchLogUserDetail(
			userId,
			{
				...filters,
				q: debouncedSearch.trim() || undefined,
				limit: PAGE_SIZE,
			},
			controller.signal,
		)
			.then((result) => {
				setDetail(result);
				setConversations(result.conversations);
				setNextCursor(result.nextCursor);
				setLoading(false);
			})
			.catch((err: unknown) => {
				if (err instanceof Error && err.name === "AbortError") return;
				const message = err instanceof Error ? err.message : "";
				setDetail(null);
				setConversations([]);
				if (message.includes("(404)")) setNotFound(true);
				else setError(message || "Gagal memuat percakapan pengguna");
				setLoading(false);
			});
		return () => controller.abort();
	}, [userId, filters, debouncedSearch]);

	const loadMore = useCallback(async () => {
		if (!nextCursor) return;
		setLoadingMore(true);
		try {
			const result = await fetchLogUserDetail(userId, {
				...filters,
				q: debouncedSearch.trim() || undefined,
				limit: PAGE_SIZE,
				cursor: nextCursor,
			});
			setConversations((prev) => [...prev, ...result.conversations]);
			setNextCursor(result.nextCursor);
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: "Gagal memuat percakapan berikutnya",
			);
		} finally {
			setLoadingMore(false);
		}
	}, [userId, filters, debouncedSearch, nextCursor]);

	return {
		detail,
		conversations,
		nextCursor,
		loading,
		loadingMore,
		error,
		notFound,
		filters,
		setFilters,
		search,
		setSearch,
		loadMore,
	};
}
